import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { EnrichedPatient } from 'data/patient-management/types';
import { useS3Uploader } from '../useS3Uploader';
import { fetchPatientsDetails } from './helper';
import i18n from '../../i18n';

export const usePatientDetails = (patientId: string) => {
  const { generateSignedUrl } = useS3Uploader();
  const { t } = useTranslation();
  const language = i18n.language || '';

  const userAccessToken = localStorage.getItem('user_access_token') || '';

  /* -------------------- QUERY -------------------- */
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['patient-details', patientId],
    enabled: Boolean(patientId && userAccessToken),
    queryFn: () =>
      fetchPatientsDetails({
        patientId,
        user_access_token: userAccessToken as string,
        generateSignedUrl,
        t,
        language
      }),
    staleTime: 5 * 60 * 1000
  });

  const patientDetails: EnrichedPatient | undefined = data?.patientUsers?.[0];

  /* -------------------- COUNTS -------------------- */
  const countList = useMemo(
    () => [
      { id: 1, name: t('organisation'), count: patientDetails?.organisationCount ?? 0 },
      { id: 2, name: t('doctors'), count: patientDetails?.doctorsCount ?? 0 },
      { id: 3, name: t('dataSafeFiles'), count: patientDetails?.dataSafeFilesCount ?? 0 },
      { id: 4, name: t('dataSafeFolders'), count: patientDetails?.dataSafeFoldersCount ?? 0 },
      { id: 5, name: t('appointment'), count: patientDetails?.appointmentsCount ?? 0 },
      { id: 6, name: t('prescriptions'), count: patientDetails?.prescriptionCount ?? 0 },
      { id: 7, name: t('feedbacksTitle'), count: patientDetails?.feedbackCount ?? 0 }
    ],
    [t, patientDetails]
  );

  /* -------------------- RETURN -------------------- */
  return {
    patientDetails,
    countList,
    folderList: patientDetails?.dataSafeFolders ?? [],
    profilePicture: patientDetails?.profilePicture,
    appointments: patientDetails?.appointmentDetails ?? [],
    doctorsTimeLine: patientDetails?.doctorsTimeLine ?? [],
    loading: isLoading,
    error: isError ? error : null
  };
};
